// EditableSynopsis - Editable text component for table cells
// maxLines: 1 = single line with ellipsis, 2-n = multi-line with clamp, 0 = unlimited

import { alpha, Box, TextField, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCreateLine } from '@/api/mutations/useLineMutations';
import { useUpdateTextAtomAnnotations } from '@/api/mutations/useTextAtomMutations';
import { filmPlanVisibilityAtom } from '@/atoms/visibility';
import { LineAutocomplete } from '@/components/LineAutocomplete/LineAutocomplete';
import { useColorMode, useGetLineColor, useRequiredAppSelector } from '@/hooks';
import type { ILine, TextAtomContentJson } from '@/types';
import { removeBracketText } from '@/utils/lineHelpers';
import { toOneLine } from '@/utils/oneLine';
import { type ContentJsonData, splitParagraphsRaw } from '@/utils/paragraphLines';
import { AnnotatedText } from './AnnotatedText';
import { LineTagPill } from './LineTagPill';

export interface EditableSynopsisProps {
    initialValue?: string;
    onSave?: (value: string) => void;
    placeholder?: string;
    fontSize?: string;
    fontWeight?: number; // Font weight for text (default: 400)
    maxLines?: number | 'dynamic'; // 1, 3, 0/undefined for unlimited, 'dynamic' for calculated based on container
    minLines?: number; // Minimum lines for dynamic calculation (default 1)
    variant?: 'logline' | 'plot'; // Controls text color: logline = gray, plot = dark
    saveStateKey?: string; // Key for save state tracking, e.g. "scene-123-logline"
    onPendingChange?: (key: string, isPending: boolean) => void; // Called when pending state changes
    useOverlayWhenEditing?: boolean; // If true, TextField uses absolute positioning (for cells that shouldn't expand)
    onEditingChange?: (isEditing: boolean) => void; // Called when editing state changes
    cellSizeMode?: 'compact' | 'medium' | 'full'; // Cell size mode to trigger recalculation on mode change
    isAnyEditing?: boolean; // Global editing state - triggers recalculation when any cell exits editing
    scenesHidden?: boolean; // Whether scene column is hidden - affects layout constraints
    readOnly?: boolean; // When true, clicking shows full text without entering edit mode
    annotationKey?: string; // Key for paragraph-line annotations (e.g. "act-5-plot")
    annotationLines?: ILine[]; // Available lines for annotation display
    textAtomId?: number; // Text atom ID for saving content_json
    contentJson?: TextAtomContentJson; // Raw content_json from text atom
    showLineTags?: boolean;
    showLineFocus?: boolean;
    /** The value is ONE line of the scene (a scene logline / plot, ADR 0008 /
     *  #1835): Enter commits like blur, and pasted line breaks collapse to
     *  spaces. */
    singleLine?: boolean;
}

export const EditableSynopsis = memo(
    ({
        initialValue = '',
        onSave,
        placeholder,
        fontSize = '12px',
        fontWeight = 400,
        maxLines = 0,
        minLines = 1,
        variant = 'plot',
        saveStateKey,
        onPendingChange,
        useOverlayWhenEditing = false,
        onEditingChange,
        cellSizeMode: _cellSizeMode,
        isAnyEditing,
        scenesHidden: _scenesHidden,
        readOnly = false,
        annotationKey,
        annotationLines,
        textAtomId,
        contentJson,
        showLineTags,
        showLineFocus,
        singleLine = false,
    }: EditableSynopsisProps) => {
        const [isEditing, setIsEditing] = useState(false);
        const [viewExpanded, setViewExpanded] = useState(false);
        const [value, setValue] = useState(initialValue);
        const isDarkMode = useColorMode();
        const getLineColor = useGetLineColor();
        const visibility = useAtomValue(filmPlanVisibilityAtom);
        const lineTagsEnabled = visibility.lineTags ?? true;
        const projectId = useRequiredAppSelector('projectId');
        const createLineMutation = useCreateLine();

        // Local content_json accumulated during editing, saved on blur
        const localContentJsonRef = useRef<string | undefined>(undefined);
        const [localContentJsonState, setLocalContentJsonState] = useState<string | undefined>(undefined);

        const saveContentJsonMutation = useUpdateTextAtomAnnotations();

        // Bracket autocomplete state
        const [bracketAC, setBracketAC] = useState<{
            anchorEl: HTMLElement;
            searchQuery: string;
            bracketOffset: number;
        } | null>(null);
        const textFieldRef = useRef<HTMLTextAreaElement>(null);
        const bracketSelectInProgressRef = useRef(false);
        const hasContent = value.trim().length > 0;
        const isPendingRef = useRef(false);
        const textRef = useRef<HTMLDivElement>(null);
        const [calculatedMaxLines, setCalculatedMaxLines] = useState<number>(1);

        // Dynamic line calculation for expandToFill cells in compact/medium mode
        useEffect(() => {
            if (maxLines !== 'dynamic' || !textRef.current) return;

            const calculateLines = () => {
                const parent = textRef.current?.parentElement;
                if (!parent) return;

                const availableHeight = parent.clientHeight;
                const fontSizePx = parseFloat(fontSize);
                const lineHeightPx = fontSizePx * 1.4; // lineHeight is 1.4
                // Use minLines as the minimum (1 for compact, 3 for medium)
                const lines = Math.max(minLines, Math.floor(availableHeight / lineHeightPx));
                setCalculatedMaxLines(lines);
            };

            // Immediate recalculation on mode change
            calculateLines();
            // Use requestAnimationFrame to recalculate after layout settles
            // This helps when cellSizeMode changes and parent height needs to update first
            const rafId = requestAnimationFrame(calculateLines);

            const observer = new ResizeObserver(calculateLines);
            if (textRef.current.parentElement) observer.observe(textRef.current.parentElement);
            return () => {
                cancelAnimationFrame(rafId);
                observer.disconnect();
            };
        }, [maxLines, fontSize, minLines]);

        // Delayed recalculation after exiting edit mode
        // When isAnyEditing changes to false, the layout switches from 'auto' to constrained grid rows
        // This delay allows the layout to settle before recalculating line counts
        useEffect(() => {
            if (maxLines !== 'dynamic' || isAnyEditing) return;

            const timeoutId = setTimeout(() => {
                const parent = textRef.current?.parentElement;
                if (!parent) return;

                const availableHeight = parent.clientHeight;
                const fontSizePx = parseFloat(fontSize);
                const lineHeightPx = fontSizePx * 1.4;
                const lines = Math.max(minLines, Math.floor(availableHeight / lineHeightPx));
                setCalculatedMaxLines(lines);
            }, 50); // Small delay for layout to settle

            return () => clearTimeout(timeoutId);
        }, [isAnyEditing, maxLines, fontSize, minLines]);

        // Determine effective maxLines value
        const effectiveMaxLines = maxLines === 'dynamic' ? calculatedMaxLines : maxLines;

        // Re-seed from the server when ITS value changes — never while editing,
        // and not merely because the field closed: the save leaves on blur and
        // takes seconds, and a field with no atom yet has nowhere in the list for
        // its words meanwhile (the create is on the wire). Seeding the old value
        // on close wiped what was just typed. Same rule as `useSynopsisField`.
        // biome-ignore lint/correctness/useExhaustiveDependencies: `isEditing` gates the seed, it must not re-trigger it — closing the field keeps what the user typed until the server's own value changes.
        useEffect(() => {
            if (!isEditing) {
                setValue(initialValue);
            }
        }, [initialValue]);

        // Notify parent of editing state changes (only on actual transitions, not on mount)
        const prevIsEditingRef = useRef(isEditing);
        useEffect(() => {
            if (prevIsEditingRef.current !== isEditing) {
                prevIsEditingRef.current = isEditing;
                onEditingChange?.(isEditing);
            }
        }, [isEditing, onEditingChange]);

        const handleChange = useCallback(
            (rawValue: string) => {
                const newValue = singleLine ? toOneLine(rawValue) : rawValue;
                setValue(newValue);

                // Signal pending state when user starts typing
                if (saveStateKey && onPendingChange && !isPendingRef.current) {
                    isPendingRef.current = true;
                    onPendingChange(saveStateKey, true);
                }

                // Detect "[" for line autocomplete
                if (lineTagsEnabled && annotationLines && annotationLines.length > 0) {
                    const bracketPos = newValue.lastIndexOf('[');
                    if (bracketPos >= 0) {
                        const cursorPos = textFieldRef.current?.selectionStart ?? newValue.length;
                        const searchQuery = newValue.substring(bracketPos + 1, cursorPos);
                        const textarea = textFieldRef.current;
                        if (textarea) {
                            setBracketAC({
                                anchorEl: textarea,
                                searchQuery,
                                bracketOffset: bracketPos,
                            });
                        }
                    } else {
                        setBracketAC(null);
                    }
                }
            },
            [singleLine, saveStateKey, onPendingChange, lineTagsEnabled, annotationLines],
        );

        const handleBlur = useCallback(() => {
            // Skip blur if a bracket select just happened (prevents premature edit-mode exit)
            if (bracketSelectInProgressRef.current) return;

            // Clear pending state
            if (saveStateKey && onPendingChange && isPendingRef.current) {
                isPendingRef.current = false;
                onPendingChange(saveStateKey, false);
            }

            // Save text if changed
            if (onSave && value !== initialValue) {
                onSave(value);
            }

            // Save content_json (same endpoint as painting mode)
            if (localContentJsonRef.current && textAtomId) {
                const parsed = JSON.parse(localContentJsonRef.current);
                saveContentJsonMutation.mutate({ project_id: projectId, id: textAtomId, content_json: parsed });
            }
            localContentJsonRef.current = undefined;
            setLocalContentJsonState(undefined);

            setIsEditing(false);
        }, [value, initialValue, onSave, saveStateKey, onPendingChange, saveContentJsonMutation.mutate, projectId, textAtomId]);

        // Clear pending state if component unmounts while editing
        useEffect(() => {
            return () => {
                if (saveStateKey && onPendingChange && isPendingRef.current) {
                    onPendingChange(saveStateKey, false);
                }
            };
        }, [saveStateKey, onPendingChange]);

        const isLogline = variant === 'logline';

        // Compute per-paragraph line associations from local or server content_json (hook must be before early returns)
        const paragraphLineMap = useMemo(() => {
            const source = localContentJsonState ?? contentJson;
            if (!source) return new Map<number, number[]>();
            try {
                const parsed = typeof source === 'string' ? JSON.parse(source) : source;
                const data = Array.isArray(parsed) ? (parsed[0] ?? {}) : parsed;
                const entries: { index: number; line_ids?: number[] }[] = data.paragraph_lines ?? [];
                return new Map(entries.filter(e => (e.line_ids?.length ?? 0) > 0).map(e => [e.index, e.line_ids as number[]]));
            } catch {
                return new Map<number, number[]>();
            }
        }, [localContentJsonState, contentJson]);

        if (isEditing) {
            // Use absolute positioning only for plots in cells that shouldn't expand (episode/act)
            const useAbsolute = useOverlayWhenEditing && !isLogline;

            // Shared helper: attach a line to content_json at the bracket position.
            // Lines in the plan attach to whole paragraphs only (#1830), so the
            // bracket position is used solely to find the paragraph index.
            const attachLineAtBracket = (lineId: number, cleanValue: string, bracketPos: number) => {
                const source = localContentJsonRef.current ?? contentJson;
                const parsed = source ? (typeof source === 'string' ? JSON.parse(source) : source) : {};
                const existing: ContentJsonData = Array.isArray(parsed) ? (parsed[0] ?? {}) : parsed;

                // splitParagraphsRaw (not parseTextIntoParagraphs) — empty paragraphs must be preserved
                // so bracket offset maps to the correct paragraph index
                const paragraphs = splitParagraphsRaw(cleanValue);
                let paragraphIndex = 0;
                let accumulated = 0;
                for (let i = 0; i < paragraphs.length; i++) {
                    const nextEnd = accumulated + (paragraphs[i]?.length ?? 0);
                    if (bracketPos <= nextEnd) {
                        paragraphIndex = i;
                        break;
                    }
                    // Skip separator characters between paragraphs
                    const remaining = cleanValue.substring(nextEnd);
                    const sepMatch = remaining.match(/^\n+/);
                    accumulated = nextEnd + (sepMatch ? sepMatch[0].length : 0);
                }

                const pLines = [...(existing.paragraph_lines ?? [])];
                const entry = pLines.find(p => p.index === paragraphIndex);
                if (entry) {
                    if (!(entry.line_ids ?? []).includes(lineId)) {
                        entry.line_ids = [...(entry.line_ids ?? []), lineId];
                    }
                } else {
                    pLines.push({ index: paragraphIndex, line_ids: [lineId] });
                }

                const newContentJson = { ...existing, paragraph_lines: pLines };
                const serialized = JSON.stringify(newContentJson);
                localContentJsonRef.current = serialized;
                setLocalContentJsonState(serialized);
            };

            const handleBracketSelect = (line: ILine) => {
                if (!textAtomId) return;
                const bracketPos = value.lastIndexOf('[');
                if (bracketPos < 0) return;

                bracketSelectInProgressRef.current = true;
                setTimeout(() => {
                    bracketSelectInProgressRef.current = false;
                }, 100);

                const cleanValue = removeBracketText(value, bracketPos, bracketAC?.searchQuery.length ?? 0);
                setValue(cleanValue);
                attachLineAtBracket(line.id, cleanValue, bracketPos);
                setBracketAC(null);

                requestAnimationFrame(() => {
                    textFieldRef.current?.focus();
                });
            };

            const handleBracketCreate = async (title: string) => {
                if (!textAtomId || !title.trim()) {
                    setBracketAC(null);
                    return;
                }

                bracketSelectInProgressRef.current = true;
                setTimeout(() => {
                    bracketSelectInProgressRef.current = false;
                }, 100);

                try {
                    const response = await createLineMutation.mutateAsync({
                        project_id: projectId,
                        title: title.trim(),
                        color: 'colorA',
                    });

                    const createdLine = response.data;
                    const bracketPos = value.lastIndexOf('[');
                    if (bracketPos < 0) {
                        setBracketAC(null);
                        return;
                    }

                    const cleanValue = removeBracketText(value, bracketPos, bracketAC?.searchQuery.length ?? 0);
                    setValue(cleanValue);
                    attachLineAtBracket(createdLine.id, cleanValue, bracketPos);
                } finally {
                    setBracketAC(null);
                    requestAnimationFrame(() => {
                        textFieldRef.current?.focus();
                    });
                }
            };

            const textField = (
                <TextField
                    multiline
                    fullWidth
                    autoFocus
                    value={value}
                    onChange={e => handleChange(e.target.value)}
                    placeholder={placeholder}
                    variant="outlined"
                    size="small"
                    onBlur={handleBlur}
                    onClick={e => e.stopPropagation()}
                    inputRef={textFieldRef}
                    onKeyDown={e => {
                        if (bracketAC) {
                            if (['Enter', 'ArrowUp', 'ArrowDown', 'Escape'].includes(e.key)) {
                                e.preventDefault();
                            }
                        } else if (singleLine && e.key === 'Enter' && !e.nativeEvent.isComposing) {
                            e.preventDefault();
                            textFieldRef.current?.blur();
                        }
                    }}
                    onFocus={e => {
                        // Move cursor to end of text
                        const len = e.target.value.length;
                        e.target.setSelectionRange(len, len);
                    }}
                    sx={{
                        ...(useAbsolute && {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 10,
                        }),
                        '& .MuiOutlinedInput-root': {
                            fontSize,
                            fontWeight,
                            backgroundColor: theme => (isDarkMode ? alpha(theme.custom.c20, 0.05) : theme.custom.c41),
                            padding: 0,
                            color: theme => (isLogline ? theme.custom.c69 : theme.custom.c68),
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '4px 6px',
                            fontWeight,
                            color: theme => (isLogline ? theme.custom.c69 : theme.custom.c68),
                            '&::placeholder': {
                                color: theme => (isDarkMode ? alpha(theme.custom.c2, 0.5) : alpha(theme.custom.c57, 0.7)),
                                opacity: 1,
                            },
                        },
                    }}
                />
            );

            // Show per-paragraph line badges above the TextField
            const paragraphs = value.split(/\n\n+/);
            const lineBadges =
                paragraphLineMap.size > 0 && annotationLines ? (
                    <div className="flex flex-col gap-[1px] mb-[2px]">
                        {paragraphs.map((text, idx) => {
                            const lineIds = paragraphLineMap.get(idx);
                            if (!lineIds || lineIds.length === 0) return null;
                            const lines = lineIds
                                .map(id => annotationLines.find(l => l.id === id))
                                .filter((l): l is ILine => !!l && l.code !== 'X');
                            if (lines.length === 0) return null;
                            const trimmed = text.trim();
                            return (
                                <div key={idx} className="flex items-center gap-[3px]">
                                    {lines.map(line => (
                                        <LineTagPill key={line.id} line={line} color={getLineColor(line.color)} />
                                    ))}
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: '10px',
                                            color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {trimmed.substring(0, 30)}
                                        {trimmed.length > 30 ? '…' : ''}
                                    </Box>
                                </div>
                            );
                        })}
                    </div>
                ) : null;

            const autocomplete =
                bracketAC && annotationLines ? (
                    <LineAutocomplete
                        lines={annotationLines.filter(l => l.code !== 'X')}
                        anchorEl={bracketAC.anchorEl}
                        searchQuery={bracketAC.searchQuery}
                        onSelect={handleBracketSelect}
                        onCreate={handleBracketCreate}
                        onClose={() => setBracketAC(null)}
                    />
                ) : null;

            // For overlay mode, wrap in relative container
            if (useAbsolute) {
                return (
                    <div className="relative h-[1.4em]">
                        {lineBadges}
                        {textField}
                        {autocomplete}
                    </div>
                );
            }

            return (
                <>
                    {lineBadges}
                    {textField}
                    {autocomplete}
                </>
            );
        }

        return (
            <Typography
                ref={textRef}
                variant="body2"
                className={hasContent ? undefined : 'placeholder-text'}
                onClick={e => {
                    e.stopPropagation();
                    if (readOnly) {
                        setViewExpanded(v => !v);
                    } else {
                        const selection = window.getSelection();
                        if (selection && !selection.isCollapsed && selection.toString().trim()) return;
                        setIsEditing(true);
                    }
                }}
                sx={{
                    cursor: readOnly ? (hasContent ? 'pointer' : 'default') : 'text',
                    opacity: hasContent ? 1 : 0,
                    // Plots use dimmer color + bold, loglines use brighter color + normal weight
                    color: theme =>
                        hasContent
                            ? isLogline
                                ? theme.custom.c69
                                : theme.custom.c68
                            : isDarkMode
                              ? alpha(theme.custom.c4, 0.6)
                              : alpha(theme.custom.c57, 0.7),
                    fontSize,
                    // Use explicit line-height in em to ensure consistent text height
                    lineHeight: '1.4em',
                    fontWeight,
                    fontStyle: hasContent ? 'normal' : 'italic',
                    // Smooth transitions for opacity and expanding/collapsing
                    transition: 'opacity 0.15s ease, max-height 0.25s ease-out',
                    // Reset default margins
                    margin: 0,
                    // Line clamping based on effectiveMaxLines: 1+=line-clamp, 0=unlimited
                    // Always use pre-wrap to preserve line breaks/paragraphs
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    ...(effectiveMaxLines >= 1 &&
                        !viewExpanded && {
                            display: '-webkit-box',
                            WebkitLineClamp: effectiveMaxLines,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }),
                }}
            >
                {hasContent ? (
                    annotationKey && annotationLines ? (
                        <AnnotatedText
                            content={value}
                            annotationKey={annotationKey}
                            lines={annotationLines}
                            textAtomId={textAtomId}
                            contentJson={contentJson}
                            showLineTags={showLineTags}
                            showLineFocus={showLineFocus}
                        />
                    ) : (
                        value
                    )
                ) : (
                    placeholder
                )}
            </Typography>
        );
    },
);

EditableSynopsis.displayName = 'EditableSynopsis';
